import React, { useState, useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { RefreshCw, CheckCircle2, XCircle, ChevronDown, BarChart2, AlertTriangle } from 'lucide-react';
import { useHistory, useHistoryMutations, useStandardQA, useEvalResult, useEvalResults, useBatches, useHealth } from '../../../hooks/console/useConsoleAPI';
import Pagination from '../shared/Pagination';
import { getVBg, getVText, getScoreColor } from '../../../constants/consoleConstants';
import { AuthContext } from '../../../../../../context/AuthContext';

const PER = 10;

const ACTION_META = {
    REGENERATE: { label: '재생성',  color: 'text-amber-600',   bg: 'bg-amber-100',   icon: RefreshCw },
    APPROVE:    { label: '승인',    color: 'text-emerald-600', bg: 'bg-emerald-100', icon: CheckCircle2 },
    REJECT:     { label: '반려',    color: 'text-red-500',     bg: 'bg-red-100',     icon: XCircle },
    ROLLBACK:   { label: '롤백',    color: 'text-gray-500',    bg: 'bg-gray-200',    icon: RefreshCw },
};

// ── 평가 결과 1건 요약 카드 ──────────────────────────────────────
// processedAction: 'APPROVE' | 'REJECT' | null — 이미 처리된 경우 버튼 대신 배지 표시
function EvalResultCard({ result, batch, onApprove, onReject, processedAction }) {
    if (!result) return null;
    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
            {batch && (
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200 flex-wrap">
                    <span className="text-xs font-semibold text-gray-600">{batch.batch_name}</span>
                    {batch.engine_version && (
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">{batch.engine_version}</span>
                    )}
                    <span className="text-xs text-gray-400 ml-auto">{batch.started_at?.slice(0, 16)}</span>
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${getVBg(result.grade)} ${getVText(result.grade)}`}>
                    {result.grade}
                </span>
                <span className={`text-base font-bold ${getScoreColor(result.final_score)}`}>
                    {result.final_score}<span className="text-xs font-normal text-gray-400">점</span>
                </span>
                <span className="text-xs text-gray-400">
                    규칙 {result.rule_score} / LLM {result.llm_score}
                </span>
                <span className="text-xs text-gray-400 ml-auto">{result.latency_ms}ms</span>
            </div>

            {result.answer_text && (
                <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">AI 답변</p>
                    <p className="text-xs text-gray-700 leading-relaxed line-clamp-3 bg-white rounded p-2 border border-gray-100">
                        {result.answer_text}
                    </p>
                </div>
            )}

            {(result.missing_terms_json ?? []).length > 0 && (
                <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">누락 키워드</p>
                    <div className="flex flex-wrap gap-1">
                        {result.missing_terms_json.map((t, i) => (
                            <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">{t}</span>
                        ))}
                    </div>
                </div>
            )}

            {(result.forbidden_hits_json ?? []).length > 0 && (
                <div className="p-2 rounded bg-red-50 border border-red-200">
                    <p className="text-xs uppercase tracking-wider text-red-500 font-bold mb-1">금지 키워드 적중</p>
                    {result.forbidden_hits_json.map((f, i) => (
                        <p key={i} className="text-xs text-red-700">"{f.text}" — {f.reason}</p>
                    ))}
                </div>
            )}

            {result.llm_eval_reason && (
                <p className="text-xs text-gray-500 italic">{result.llm_eval_reason}</p>
            )}

            {/* 처리 상태 영역 */}
            <div className="flex gap-2 pt-2 border-t border-gray-200 items-center">
                {processedAction ? (
                    // 이미 처리된 결과 — 배지만 표시
                    processedAction === 'APPROVE'
                        ? <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-medium">
                            <CheckCircle2 size={11} /> 승인됨
                          </span>
                        : <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-red-100 text-red-600 font-medium">
                            <XCircle size={11} /> 반려됨
                          </span>
                ) : (
                    // 미처리 — 승인/반려 버튼 표시
                    <>
                        {onApprove && (
                            <button
                                onClick={() => onApprove(result.id)}
                                className="text-xs px-3 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-1"
                            >
                                <CheckCircle2 size={10} /> 이 결과 승인
                            </button>
                        )}
                        {onReject && (
                            <button
                                onClick={() => onReject(result.id)}
                                className="text-xs px-3 py-1 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center gap-1"
                            >
                                <XCircle size={10} /> 이 결과 반려
                            </button>
                        )}
                    </>
                )}
                <span className="text-xs text-gray-400 flex items-center ml-auto">결과 ID: {result.id}</span>
            </div>
        </div>
    );
}

// ── qa_standard_id로 최근 평가 결과 + 해당 QA 전체 이력 조회 ──────────────────
const RESULT_STEP = 5;

function EvalResultByQaId({ qaId, openModal }) {
    const [size, setSize] = useState(RESULT_STEP);

    const { data, isLoading, isFetching } = useEvalResults({ qa_standard_id: qaId, size, page: 1 });
    const { data: batchData } = useBatches({ size: 500 });
    // 이 QA의 이력 전체 조회 → 어떤 result가 이미 승인/반려됐는지 파악
    const { data: qaHistData } = useHistory({ qa_standard_id: qaId, size: 500 });

    const items   = data?.items ?? [];
    const total   = data?.total ?? 0;
    const batches = batchData?.items ?? [];
    const hasMore = items.length < total;

    // evaluation_result_id → action_type (APPROVE/REJECT) 매핑
    const processedMap = new Map(
        (qaHistData?.items ?? [])
            .filter((r) => (r.action_type === 'APPROVE' || r.action_type === 'REJECT') && r.evaluation_result_id)
            .map((r) => [r.evaluation_result_id, r.action_type])
    );

    if (isLoading) return <p className="text-xs text-gray-400 text-center py-2">최근 평가 결과 불러오는 중...</p>;
    if (items.length === 0) return (
        <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
            <AlertTriangle size={12} className="text-amber-400" />
            아직 이 QA의 평가 결과가 없습니다.
        </div>
    );

    return (
        <div className="space-y-2">
            <p className="text-xs text-gray-400">
                평가 결과 {items.length} / {total}건 — 최신순
            </p>
            {items.map((r) => {
                const batch = batches.find((b) => b.id === r.batch_id) ?? null;
                const processedAction = processedMap.get(r.id) ?? null;
                return (
                    <EvalResultCard
                        key={r.id}
                        result={r}
                        batch={batch}
                        processedAction={processedAction}
                        onApprove={processedAction ? null : (resultId) => openModal('APPROVE', qaId, resultId)}
                        onReject={processedAction ? null : (resultId) => openModal('REJECT', qaId, resultId)}
                    />
                );
            })}
            {hasMore && (
                <button
                    onClick={() => setSize((s) => s + RESULT_STEP)}
                    disabled={isFetching}
                    className="w-full py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                    {isFetching ? '불러오는 중...' : `더보기 (${total - items.length}건 남음)`}
                </button>
            )}
        </div>
    );
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────
export default function HistoryTab() {
    const { auth } = useContext(AuthContext);
    const { data: healthData } = useHealth();
    const [params, setParams] = useState({ page: 1, size: PER });
    const [filterAction, setFilterAction] = useState('');
    const [filterQaId, setFilterQaId] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('REGENERATE');
    const [modalForm, setModalForm] = useState({ qa_standard_id: '', evaluation_result_id: '', engine_version: '', action_note: '', acted_by: '' });

    const histParams = {
        ...params,
        ...(filterAction ? { action_type: filterAction } : {}),
        ...(filterQaId  ? { qa_standard_id: filterQaId } : {}),
    };
    const { data: histData, isLoading } = useHistory(histParams);
    // QA 500개까지 로드 (100개 한도 제거)
    const { data: qaData } = useStandardQA({ size: 500 });

    const records  = histData?.items ?? [];
    const total    = histData?.total ?? 0;
    const totalPages = Math.ceil(total / PER);
    const qaList   = qaData?.items ?? [];

    const { regenerate, approve, reject } = useHistoryMutations();

    const getQA = (id) => qaList.find((q) => String(q.id) === String(id));

    const openModal = (type, qaId = '', resultId = '') => {
        setModalType(type);
        setModalForm({
            qa_standard_id: String(qaId),
            evaluation_result_id: String(resultId),
            engine_version: healthData?.engine_version ?? '',
            action_note: '',
            acted_by: auth?.userId ?? '',
        });
        setShowModal(true);
    };

    const handleAction = () => {
        if (!modalForm.qa_standard_id || !modalForm.acted_by) return;
        // 승인·반려는 evaluation_result_id 필수 (API 스펙 5-3, 5-4)
        if ((modalType === 'APPROVE' || modalType === 'REJECT') && !modalForm.evaluation_result_id) return;

        let payload;
        if (modalType === 'REGENERATE') {
            // 5-2: qa_standard_id(Y), evaluation_result_id(N), engine_version(N), acted_by(Y), action_note(N)
            payload = {
                qa_standard_id: Number(modalForm.qa_standard_id),
                ...(modalForm.evaluation_result_id ? { evaluation_result_id: Number(modalForm.evaluation_result_id) } : {}),
                ...(modalForm.engine_version       ? { engine_version: modalForm.engine_version } : {}),
                acted_by: modalForm.acted_by,
                ...(modalForm.action_note          ? { action_note: modalForm.action_note } : {}),
            };
        } else {
            // 5-3, 5-4: qa_standard_id(Y), evaluation_result_id(Y), acted_by(Y), action_note(N)
            payload = {
                qa_standard_id:      Number(modalForm.qa_standard_id),
                evaluation_result_id: Number(modalForm.evaluation_result_id),
                acted_by: modalForm.acted_by,
                ...(modalForm.action_note ? { action_note: modalForm.action_note } : {}),
            };
        }

        const mutation = modalType === 'REGENERATE' ? regenerate
            : modalType === 'APPROVE' ? approve
            : reject;
        mutation.mutate(payload, { onSuccess: () => setShowModal(false) });
    };

    const isMutating = regenerate.isLoading || approve.isLoading || reject.isLoading;

    return (
        <div className="space-y-4 w-full">
            {/* 헤더 */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                    <p className="text-lg font-bold text-sb-header">처리 이력</p>
                    <p className="text-base text-gray-400">AI 답변에 대해 처리한 내역(재생성·승인·반려)을 확인합니다. 일반적으로 결과 검토 탭에서 처리하는 것을 권장합니다.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => openModal('REGENERATE')} className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors">
                        <RefreshCw size={13} /> 재생성 요청
                    </button>
                    <button onClick={() => openModal('APPROVE')} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors">
                        <CheckCircle2 size={13} /> 승인
                    </button>
                    <button onClick={() => openModal('REJECT')} className="flex items-center gap-1.5 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors">
                        <XCircle size={13} /> 반려
                    </button>
                </div>
            </div>

            {/* 필터 */}
            <div className="flex flex-wrap gap-2">
                {['', 'REGENERATE', 'APPROVE', 'REJECT', 'ROLLBACK'].map((a) => (
                    <button
                        key={a}
                        onClick={() => { setFilterAction(a); setParams((p) => ({ ...p, page: 1 })); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterAction === a ? 'bg-sb-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                        {a === '' ? '전체' : ACTION_META[a]?.label}
                    </button>
                ))}
                <select
                    value={filterQaId}
                    onChange={(e) => { setFilterQaId(e.target.value); setParams((p) => ({ ...p, page: 1 })); }}
                    className="px-3 py-1.5 rounded-lg text-xs border border-gray-200 bg-white outline-none focus:border-sb-primary"
                >
                    <option value="">전체 QA</option>
                    {qaList.map((q) => (
                        <option key={q.id} value={q.id}>{q.qa_code} — {q.question.slice(0, 20)}...</option>
                    ))}
                </select>
            </div>

            {/* 이력 목록 */}
            <div className="space-y-2">
                {isLoading && <p className="text-center py-8 text-sm text-gray-400">불러오는 중...</p>}
                {!isLoading && records.map((rec) => {
                    const am   = ACTION_META[rec.action_type] ?? ACTION_META.REGENERATE;
                    const AIcon = am.icon;
                    const qa   = getQA(rec.qa_standard_id);
                    const isOpen  = selectedId === rec.id;
                    const isRegen = rec.action_type === 'REGENERATE';

                    return (
                        <div key={rec.id} className="rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all overflow-hidden">
                            {/* 헤더 행 */}
                            <div className="p-4 cursor-pointer" onClick={() => setSelectedId(isOpen ? null : rec.id)}>
                                <div className="flex items-center gap-3">
                                    <div className={`p-1.5 rounded-full ${am.bg}`}>
                                        <AIcon size={14} className={am.color} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${am.bg} ${am.color}`}>{am.label}</span>
                                            {rec.final_grade && (
                                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${getVBg(rec.final_grade)} ${getVText(rec.final_grade)}`}>{rec.final_grade}</span>
                                            )}
                                            {qa && <span className="text-xs font-mono text-gray-400">{qa.qa_code}</span>}
                                            {isRegen && (
                                                <span className="text-xs text-gray-400 flex items-center gap-0.5">
                                                    <BarChart2 size={10} /> 평가 결과 포함
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-sb-header truncate">
                                            {qa ? qa.question : `QA #${rec.qa_standard_id}`}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-xs text-gray-500">{rec.acted_by}</p>
                                        <p className="text-xs text-gray-400">{rec.acted_at?.slice(0, 10)}</p>
                                    </div>
                                    <ChevronDown size={14} className={`text-gray-400 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                                </div>
                            </div>

                            {/* 펼침 영역 */}
                            {isOpen && (
                                <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                                    <div className="flex gap-4 text-xs text-gray-500 flex-wrap">
                                        <span>처리자: <span className="font-medium">{rec.acted_by}</span></span>
                                        <span>처리 시각: <span className="font-medium">{rec.acted_at}</span></span>
                                    </div>

                                    {rec.action_note && (
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">메모</p>
                                            <p className="text-xs text-gray-700">{rec.action_note}</p>
                                        </div>
                                    )}

                                    {/* 재생성 → 새로 생성된 최신 평가 결과 표시
                                        rec.evaluation_result_id = 이전(구) 결과 ID (백엔드 previous_result_id)
                                        새 결과(new_result_id)는 목록 API에서 반환하지 않으므로
                                        qa_standard_id 기준 최신순 조회로 새 결과를 표시 */}
                                    {isRegen && (
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">재생성된 평가 결과 (최신순)</p>
                                            <EvalResultByQaId
                                                qaId={rec.qa_standard_id}
                                                openModal={openModal}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {!isLoading && records.length === 0 && (
                    <div className="text-center py-10 space-y-1">
                        <p className="text-base font-medium text-gray-500">
                            {filterAction || filterQaId ? '선택한 조건에 맞는 이력이 없습니다.' : '아직 처리 이력이 없습니다.'}
                        </p>
                        {!filterAction && !filterQaId && (
                            <p className="text-sm text-gray-400">결과 검토 탭에서 항목을 검토하고 처리하면 이곳에 기록됩니다.</p>
                        )}
                    </div>
                )}
            </div>

            <Pagination currentPage={params.page} totalPages={totalPages} onPageChange={(p) => setParams((prev) => ({ ...prev, page: p }))} />

            {/* 액션 모달 */}
            <Modal show={showModal} centered onHide={() => setShowModal(false)} contentClassName="rounded-2xl border-0 shadow-xl">
                <Modal.Header className="border-b border-gray-100 px-6 py-3">
                    <Modal.Title className="text-sm font-bold text-sb-header">
                        {ACTION_META[modalType]?.label} 처리
                    </Modal.Title>
                    <button type="button" className="modal-btn-close" onClick={() => setShowModal(false)}>
                        <span className="modal-btn-span">X</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="px-6 py-4 space-y-3">
                    {/* 대상 QA — 공통 필수 */}
                    <div>
                        <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1">대상 QA *</label>
                        <select
                            value={modalForm.qa_standard_id}
                            onChange={(e) => setModalForm({ ...modalForm, qa_standard_id: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg text-xs border border-gray-200 bg-white outline-none focus:border-sb-primary"
                        >
                            <option value="">QA 선택</option>
                            {qaList.map((q) => (
                                <option key={q.id} value={q.id}>{q.qa_code} — {q.question}</option>
                            ))}
                        </select>
                    </div>

                    {/* 재생성: 엔진 버전 선택 (선택) */}
                    {modalType === 'REGENERATE' && (
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1">엔진 버전 (선택)</label>
                            <input
                                value={modalForm.engine_version}
                                onChange={(e) => setModalForm({ ...modalForm, engine_version: e.target.value })}
                                placeholder="예: v3_r4"
                                className="w-full px-3 py-2 rounded-lg text-xs border border-gray-200 bg-white outline-none focus:border-sb-primary font-mono"
                            />
                        </div>
                    )}

                    {/* 승인·반려: 평가 결과 ID 필수 */}
                    {(modalType === 'APPROVE' || modalType === 'REJECT') && (
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1">평가 결과 ID <span className="text-blue-400 normal-case font-normal">(평가 결과 카드에서 버튼 클릭 시 자동 입력됩니다)</span></label>
                            <input
                                type="number"
                                value={modalForm.evaluation_result_id}
                                onChange={(e) => setModalForm({ ...modalForm, evaluation_result_id: e.target.value })}
                                placeholder="해당 평가 결과 ID"
                                className="w-full px-3 py-2 rounded-lg text-xs border border-gray-200 bg-white outline-none focus:border-sb-primary"
                            />
                            <p className="text-xs text-gray-400 mt-1">평가 결과 카드 하단의 "결과 ID"를 입력하세요.</p>
                        </div>
                    )}

                    {/* 처리자 ID — 공통 필수 */}
                    <div>
                        <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1">처리자</label>
                        <span className="flex items-center px-3 py-2 rounded-lg text-xs border border-gray-100 bg-gray-50 text-gray-600">
                            {modalForm.acted_by || '로그인 필요'}
                        </span>
                    </div>

                    {/* 메모 — 공통 선택 */}
                    <div>
                        <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1">메모 (선택)</label>
                        <textarea
                            value={modalForm.action_note}
                            onChange={(e) => setModalForm({ ...modalForm, action_note: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg text-xs border border-gray-200 bg-white outline-none focus:border-sb-primary resize-y"
                            placeholder={
                                modalType === 'REGENERATE' ? '재생성 사유 또는 적용 정책 설명'
                                : modalType === 'APPROVE'  ? '승인 확인 내용'
                                : '반려 사유'
                            }
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 px-6 pb-4 pt-0">
                    <Button variant="light" className="text-gray-600" onClick={() => setShowModal(false)}>취소</Button>
                    <Button
                        className="border-0 text-white"
                        style={{ backgroundColor: modalType === 'APPROVE' ? '#059669' : modalType === 'REJECT' ? '#ef4444' : '#f59e0b' }}
                        onClick={handleAction}
                        disabled={isMutating}
                    >
                        {isMutating ? '처리 중...' : ACTION_META[modalType]?.label}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
